import React from 'react'
import { Bullet, Bullets, Info, Page, Section, Step } from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const SharingAndAccessLogs = () => (
  <Page
    title="Sharing and Access Logs"
    intro="ePCRs support controlled sharing so authorised people outside the record's owner can view a specific record, and every access is logged."
  >
    <Section title="Attachments and image previews">
      <p>
        ePCRs support file attachments with image previews, so photographs and
        supporting documents can be reviewed alongside the clinical record.
      </p>
    </Section>

    <Section title="Share codes">
      <Bullets>
        <Bullet>
          A share code gives an authorised external viewer controlled access
          to a specific record without giving them a full AmbuNet account.
          Codes are created from the <strong>Share codes</strong> panel in
          the ePCR&apos;s Sharing module (web or mobile) or from the report
          view. Choose how long the code works for — 4, 12 or 24 hours — and
          it expires automatically after that.
        </Bullet>
        <Bullet>
          Share codes can be emailed to the intended recipient when they are
          created, or later from the code&apos;s details, along with a link
          to the access page. The code and the access link can also be
          copied to the clipboard.
        </Bullet>
        <Bullet>
          Each code shows whether it is active, expired or revoked, how many
          times it has been used to download the record, and any refused
          attempts with the reason they were refused.
        </Bullet>
        <Bullet>
          Access can be revoked at any time, which immediately stops the
          share code from working. Creating and revoking a code are both
          written to the record&apos;s access log.
        </Bullet>
      </Bullets>
      <Info title="Who can do this?">
        Only the record&apos;s current owner (or its creator, while no one
        else owns it) can create a share code, and not while the record is
        handed over to a treatment location. The owner, the Governance team
        and site admins can see a record&apos;s codes and revoke them. Share
        codes are a tier 2 subscription feature.
      </Info>
    </Section>

    <Section title="Public access links">
      <p>
        The recipient opens{' '}
        <DocLink url="/access-record" text="Access Record" /> — a public page
        that needs no login — and is verified before anything is shown:
      </p>
      <Step number={1} title="Enter the share code">
        Along with their own name and organisation, which are recorded
        against the attempt.
      </Step>
      <Step number={2} title="Confirm the patient's details">
        The patient's surname and date of birth (or their &quot;unknown&quot;
        flags) must match the record exactly, and the page is protected by
        reCAPTCHA.
      </Step>
      <Step number={3} title="View the record">
        Only submitted records are shown — a record still in draft cannot be
        viewed through a share code. Every attempt, successful or failed
        (with the reason it failed), is logged against the share code.
      </Step>
    </Section>

    <Section title="Access logs">
      <p>
        Every time a report is opened, updated, or downloaded as a PDF — by
        the owning clinician, a treatment location, a governance reviewer or
        an external visitor using a share code — an access log entry is
        recorded with who accessed it and why.
      </p>
      <Bullets>
        <Bullet>
          <strong>For one record:</strong> choose <strong>Access log</strong>{' '}
          from the menu in the report view header. A side panel lists every
          entry by day, and the chips at the top filter by activity — views,
          PDF downloads, share-code downloads, share codes created or
          revoked, and updates.
        </Bullet>
        <Bullet>
          <strong>For all records:</strong> the{' '}
          <DocLink url="/patient-report-forms?view=access-logs" text="Access log" />{' '}
          tab on the Patient Care Records dashboard shows activity across
          every record, with filters for date, activity type, staff member,
          external visitors and record number or patient surname. Rows open
          the record in a new tab, and the grid can be exported.
        </Bullet>
      </Bullets>
      <Info title="Who can do this?">
        The dashboard access log is part of the Patient Care Records
        dashboard, so it needs the <strong>Governance</strong> team and a
        verified (MFA) session.
      </Info>
    </Section>

    <Section title="Clinical Advice Line notes">
      <p>
        Records can carry Clinical Advice Line (CAL) notes added after
        submission, giving a way to document follow-up guidance without
        editing the original clinical entry. Each note has a &quot;show in
        record&quot; toggle: notes flagged to show are visible to the
        clinician who wrote the record, while unflagged notes (including the
        automatic safeguarding review entries) are visible to governance
        reviewers only.
      </p>
      <Info title="Who can do this?">
        Adding CAL notes and changing their visibility requires the{' '}
        <strong>Governance</strong> team. Any user who can open the record can
        read the notes flagged to show in the record.
      </Info>
    </Section>
  </Page>
)

export default SharingAndAccessLogs
